/**
 * Orchestrates the API calls for the MappingPage:
 *
 * 1. Fetch target columns (schema definition)
 * 2. Fetch known mappings
 * 3. Call processFile (Traffic Light pipeline) for all fields
 * 4. Return transformed MappingRow[] + schema lookups
 */

import { useState, useEffect, useRef } from "react";
import { fetchSchemaFields, fetchKnownMappings, processFile } from "@/services/api";
import { buildSchemaLookups, type SchemaLookups } from "@/lib/schema";
import { useUpload } from "@/hooks/useUpload";
import { useTelemetry } from "@/hooks/useTelemetry";
import type { MappingRow } from "@/types";

export interface ProcessingState {
  loading: boolean;
  error: string | null;
  /** All mapping rows (mandatory + additional combined) */
  rows: MappingRow[];
  /** Source column names from the parsed file */
  sourceColumns: string[];
  /** Column names that appear more than once in the uploaded file */
  duplicateColumns: string[];
  /** Schema lookups built from target columns */
  schema: SchemaLookups;
  processingTimeMs: number;
}

const EMPTY_SCHEMA = buildSchemaLookups([]);

export function useProcessing(): ProcessingState {
  const { file, schemaId, inputColumns, documentId, reportType } = useUpload();
  const { track, trackError, startTimer } = useTelemetry();
  const ran = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [sourceColumns, setSourceColumns] = useState<string[]>([]);
  const [duplicateColumns, setDuplicateColumns] = useState<string[]>([]);
  const [schema, setSchema] = useState<SchemaLookups>(EMPTY_SCHEMA);
  const [processingTimeMs, setProcessingTimeMs] = useState(0);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const stopTimer = startTimer();

    (async () => {
      try {
        // 1. Fetch target columns (schema definition).
        // Pass reportType so the backend returns is_mandatory scoped to the
        // selected contract — only fields mandatory for that report_type appear
        // in the mandatory tab rather than all fields mandatory for any type.
        const schemaFields = await fetchSchemaFields(
          schemaId ?? undefined,
          undefined,
          reportType ?? undefined
        );
        const lookups = buildSchemaLookups(schemaFields);
        setSchema(lookups);

        track("processing_schema_loaded", { fieldCount: schemaFields.length });

        // 2. Fetch known mappings (scoped to schema when available)
        const knownMappings = await fetchKnownMappings(schemaId ?? undefined);
        track("processing_known_mappings_loaded", { count: knownMappings.length });

        // 3. Process file through the Traffic Light pipeline — all fields at once
        const effectiveFileId = documentId || crypto.randomUUID();
        const effectiveFile = file ?? new File([""], "empty.csv");

        if (!file) {
          throw new Error("No file available to process. Please upload a file first.");
        }

        track("Processing.Initiated", {
          documentId: effectiveFileId,
          fileName: effectiveFile.name,
          schemaId: schemaId || "unknown",
          fieldCount: schemaFields.length,
          knownMappingCount: knownMappings.length,
          reportType: reportType ?? "unset",
        });

        // reportType is the user-confirmed File Type from the Preview
        // screen (seeded by the classification cascade). Passing it to
        // /process lets the backend filter schema_fields to the correct
        // contract (claims vs risk_premiums) before mapping runs.
        const result = await processFile(
          effectiveFile,
          effectiveFileId,
          schemaFields,
          knownMappings,
          reportType
        );

        setRows(result.rows);
        setSourceColumns(result.sourceColumns.length > 0 ? result.sourceColumns : inputColumns);
        setDuplicateColumns(result.duplicateColumns);
        setProcessingTimeMs(result.processingTimeMs);

        const durationMs = stopTimer();
        track("Processing.Succeeded", {
          documentId: effectiveFileId,
          mappingCount: result.rows.length,
          processingTimeMs: result.processingTimeMs,
          durationMs,
        });
      } catch (err) {
        const durationMs = stopTimer();
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);

        track("Processing.Failed", {
          documentId: documentId || "unknown",
          errorMessage: msg,
          durationMs,
        });
        trackError(err, { context: "processing_pipeline" });
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { loading, error, rows, sourceColumns, duplicateColumns, schema, processingTimeMs };
}
